import apiClient from './client';
import type { CvMetadata, CvUploadResponse, PaginatedResponse, CvStatus, LocalFolderStatus } from '../types';

// -------------------------------------------------------------------------
// Query params
// -------------------------------------------------------------------------

export interface CvListParams {
  page?: number;
  page_size?: number;
  status?: CvStatus;
  search?: string;
  employee_id?: string;
  sort_by?: 'upload_date' | 'employee_name';
  order?: 'asc' | 'desc';
}

// -------------------------------------------------------------------------
// Backend → frontend mapping
// -------------------------------------------------------------------------

function parseSkills(value: unknown): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.startsWith('[')) {
    try { return JSON.parse(value); } catch { /* fall through */ }
  }
  return [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapCv(raw: any): CvMetadata {
  return {
    id: raw.id,
    employee_id: raw.employee_id ?? '',
    employee_name: raw.employee_name ?? raw.file_name?.split('_').slice(0, 2).join(' ') ?? '',
    file_name: raw.file_name ?? '',
    file_size: raw.file_size ?? 0,
    mime_type: raw.content_type ?? raw.mime_type ?? '',
    upload_date: raw.created_at ?? raw.upload_date ?? '',
    status: raw.status ?? 'pending_review',
    extracted_skills: parseSkills(raw.raw_skills ?? raw.extracted_skills),
    version: raw.version ?? 1,
    anonymized: raw.anonymized ?? (raw.status === 'gdpr_erased'),
  };
}

// -------------------------------------------------------------------------
// API functions
// -------------------------------------------------------------------------

/**
 * Fetch a paginated list of CV metadata records.
 * Maps Spring Page response to frontend PaginatedResponse.
 */
export async function fetchCvs(params: CvListParams = {}): Promise<PaginatedResponse<CvMetadata>> {
  const sortField = params.sort_by === 'upload_date' ? 'createdAt' : params.sort_by ?? 'createdAt';
  const springParams: Record<string, unknown> = {
    page: (params.page ?? 1) - 1,
    size: params.page_size ?? 20,
    ...(params.search ? { q: params.search } : {}),
    ...(params.status ? { status: params.status } : {}),
    ...(params.employee_id ? { employeeId: params.employee_id } : {}),
    sort: `${sortField},${params.order ?? 'desc'}`,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await apiClient.get<any>('/cvs', { params: springParams });

  return {
    items: (data.content ?? []).map(mapCv),
    total: data.total_elements ?? 0,
    page: (data.number ?? 0) + 1,
    page_size: data.size ?? 20,
    total_pages: data.total_pages ?? 0,
  };
}

/**
 * Fetch CV metadata for a single CV by ID.
 */
export async function fetchCv(cvId: string): Promise<CvMetadata> {
  const { data } = await apiClient.get<CvMetadata>(`/cvs/${cvId}`);
  return data;
}

/**
 * Upload a CV file for a given employee.
 * Uses multipart/form-data.
 */
export async function uploadCv(
  email: string,
  file: File,
  onUploadProgress?: (percent: number) => void
): Promise<CvUploadResponse> {
  const formData = new FormData();
  formData.append('email', email);
  formData.append('file', file);

  const { data } = await apiClient.post<CvUploadResponse>('/cvs/upload', formData, {
    headers: { 'Content-Type': undefined },
    onUploadProgress: (event) => {
      if (onUploadProgress && event.total) {
        onUploadProgress(Math.round((event.loaded * 100) / event.total));
      }
    },
  });
  return data;
}

/**
 * Update CV status (e.g., activate, deactivate).
 */
export async function updateCvStatus(cvId: string, status: CvStatus): Promise<CvMetadata> {
  const { data } = await apiClient.patch<CvMetadata>(`/cvs/${cvId}/status`, { status });
  return data;
}

/**
 * Request anonymisation of a CV (DPO / GDPR workflow).
 */
export async function anonymiseCv(cvId: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/cvs/${cvId}/anonymise`);
  return data;
}

/**
 * Reassign a CV to a different employee.
 */
export async function reassignCv(cvId: string, employeeId: string): Promise<void> {
  await apiClient.put(`/cvs/${cvId}/reassign`, { employeeId });
}

/**
 * Delete a CV record permanently.
 */
export async function deleteCv(cvId: string): Promise<void> {
  await apiClient.delete(`/cvs/${cvId}`);
}

/**
 * Download a CV file (returns a Blob).
 */
export async function downloadCv(cvId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/cvs/${cvId}/download`, {
    responseType: 'blob',
  });
  return data;
}

/**
 * Fetch the current status of the local folder ingestion watcher.
 */
export async function getLocalFolderStatus(): Promise<LocalFolderStatus> {
  const { data } = await apiClient.get<LocalFolderStatus>('/cvs/local-folder/status');
  return data;
}

/**
 * Trigger an immediate scan of the local inbox folder.
 */
export async function triggerLocalFolderScan(): Promise<void> {
  await apiClient.post('/cvs/local-folder/scan');
}

export interface UnmatchedFile {
  item_id: string;
  file_name: string;
  size: number;
}

export interface OneDriveSyncResult {
  total_files: number;
  ingested: number;
  skipped_duplicate: number;
  skipped_no_match: number;
  failed: number;
  unmatched_files: UnmatchedFile[];
}

/**
 * Import CVs from the configured OneDrive folder.
 */
export async function syncCvsFromOneDrive(): Promise<OneDriveSyncResult> {
  const { data } = await apiClient.post<OneDriveSyncResult>('/cvs/sync-onedrive');
  return data;
}

/**
 * Fetch all persisted unmatched OneDrive files awaiting manual assignment.
 */
export async function fetchUnmatchedFiles(): Promise<UnmatchedFile[]> {
  const { data } = await apiClient.get<UnmatchedFile[]>('/cvs/unmatched-onedrive');
  // Backend uses camelCase; normalise to snake_case used by the frontend type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data as any[]).map((f) => ({
    item_id: f.itemId ?? f.item_id,
    file_name: f.fileName ?? f.file_name,
    size: f.size ?? 0,
  }));
}

/**
 * Manually assign an unmatched OneDrive file to an employee.
 */
export async function assignOneDriveFile(
  itemId: string,
  fileName: string,
  employeeId: string
): Promise<void> {
  await apiClient.post('/cvs/assign-onedrive', {
    itemId,
    fileName,
    employeeId,
  });
}
